import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Brand } from "@/components/Brand";

const BG = "https://images.unsplash.com/photo-1631477076114-9123f721b9dc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxkYXJrJTIwbWluaW1hbCUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3OTA4NzY0ODd8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, login, status, loadStatus } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { if (user) navigate("/"); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Unable to sign in.");
    } finally {
      setBusy(false);
    }
  };

  const setupPending = status && status.owner_email_set && !status.owner_configured;

  return (
    <div className="flex min-h-screen bg-[#121211] text-[#F9F9F8]">
      <div className="relative hidden w-1/2 lg:block">
        <img src={BG} alt="" className="h-full w-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#121211] via-transparent to-[#121211]/40" />
        <div className="absolute bottom-10 left-10">
          <Brand logoHeight="h-14" mediaSize="text-[13px]" />
          <div className="cm-label mt-3">Private film operations · Invite only</div>
        </div>
      </div>

      <div className="flex w-full items-center justify-center px-6 lg:w-1/2">
        <div className="w-full max-w-sm">
          <div className="font-display text-3xl tracking-tight">Sign in</div>
          <p className="mt-2 text-sm text-[#A1A19D]">
            Access is restricted to invited accounts. There is no public sign-up.
          </p>

          {setupPending && (
            <div className="mt-6 border border-[rgba(245,158,11,0.3)] bg-[rgba(245,158,11,0.1)] p-4 text-sm" data-testid="setup-pending-notice">
              <div className="cm-label text-[#F59E0B]">Setup pending</div>
              <p className="mt-1 text-[#F9F9F8]">
                The owner password has not been configured. The application is locked until an owner is securely bootstrapped.
              </p>
              {status.bootstrap_available && (
                <Link to="/setup" className="mt-2 inline-block text-[#F59E0B] underline-offset-2 hover:underline" data-testid="setup-link">
                  Configure owner access →
                </Link>
              )}
            </div>
          )}

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="cm-label">Email</label>
              <input
                data-testid="login-email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-sm border border-[#2C2C2A] bg-[#1A1A18] px-3 py-2.5 text-sm text-[#F9F9F8] focus:outline-none focus:ring-2 focus:ring-[#F59E0B]"
                required
              />
            </div>
            <div>
              <label className="cm-label">Password</label>
              <input
                data-testid="login-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full rounded-sm border border-[#2C2C2A] bg-[#1A1A18] px-3 py-2.5 text-sm text-[#F9F9F8] focus:outline-none focus:ring-2 focus:ring-[#F59E0B]"
                required
              />
            </div>
            {error && <div className="text-sm text-[#e07a7a]" data-testid="login-error">{error}</div>}
            <button
              data-testid="login-submit"
              type="submit"
              disabled={busy}
              className="w-full rounded-sm bg-[#F59E0B] px-4 py-2.5 text-sm font-semibold text-[#121211] transition-colors hover:bg-[#e8940a] disabled:opacity-60"
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
